/**
 * Busca de endereco pelo CEP, no ViaCEP.
 *
 * A view declara os campos por seletor, e nao por id fixo:
 *
 *   <input data-mascara="cep" data-cep
 *          data-cep-logradouro="#Logradouro"
 *          data-cep-bairro="#Bairro"
 *          data-cep-uf="#Uf"
 *          data-cep-municipio="#CodigoIbgeMunicipio"
 *          data-cep-foco="#Numero" />
 *
 * O WashFlow faz o mesmo com ids literais repassados pelo `.ts` da pagina. Aqui
 * o acoplamento e por atributo, como todo o resto dos comportamentos globais —
 * e assim o campo continua funcionando se um dia o ViewModel aninhar o
 * endereco, quando o `asp-for` passa a gerar `Endereco_Cep` em vez de `Cep`.
 *
 * **Chamada do navegador direto para o ViaCEP**, sem passar pelo servidor. E o
 * que o WashFlow ja faz, o ViaCEP permite CORS, e assim uma indisponibilidade
 * do servico nao ocupa uma thread da nossa aplicacao. O custo e depender da
 * rede de quem preenche — por isso toda falha e silenciosa no sentido de nao
 * travar nada: o formulario continua preenchivel a mao.
 */

const VIACEP = 'https://viacep.com.br/ws';

interface RespostaViaCep {
    erro?: boolean | string;
    logradouro?: string;
    bairro?: string;
    localidade?: string;
    uf?: string;
    /** Codigo do municipio no IBGE — a mesma chave que gravamos no cliente. */
    ibge?: string;
}

function somenteDigitos(valor: string): string {
    return valor.replace(/\D/g, '');
}

function alvo(campo: HTMLInputElement, chave: string): HTMLInputElement | HTMLSelectElement | null {
    const seletor = campo.dataset[chave];

    return seletor ? document.querySelector<HTMLInputElement | HTMLSelectElement>(seletor) : null;
}

function preencher(elemento: HTMLInputElement | HTMLSelectElement | null, valor: string): void {
    if (elemento && valor) elemento.value = valor;
}

function mensagem(campo: HTMLInputElement, texto: string): void {
    campo.classList.toggle('is-invalid', texto.length > 0);

    let aviso = campo.parentElement?.querySelector<HTMLElement>('.js-cep-aviso') ?? null;

    if (!aviso) {
        if (!texto) return;

        aviso = document.createElement('div');
        aviso.className = 'invalid-feedback js-cep-aviso';
        campo.parentElement?.appendChild(aviso);
    }

    aviso.textContent = texto;
}

/**
 * O municipio e um `<select>` com busca remota (ver `selecao.ts`), entao nao
 * basta atribuir o valor: a opcao precisa existir. O ViaCEP devolve o codigo do
 * IBGE, que e exatamente a nossa chave — dai da para montar a opcao na hora, sem
 * ida extra ao servidor.
 *
 * E a UF vai **antes**, com o evento disparado na mao: e a troca de UF que
 * limpa e reabilita o campo de municipio. Preencher na ordem inversa faria a
 * limpeza apagar o municipio recem-escolhido.
 */
function preencherMunicipio(campo: HTMLInputElement, dados: RespostaViaCep): void {
    const campoUf = alvo(campo, 'cepUf');
    const campoMunicipio = alvo(campo, 'cepMunicipio');

    if (campoUf && dados.uf && campoUf.value !== dados.uf) {
        campoUf.value = dados.uf;
        campoUf.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (!campoMunicipio || !dados.ibge || !dados.localidade) return;

    const selecao = (campoMunicipio as unknown as { tomselect?: TomSelectMinimo }).tomselect;

    if (!selecao) {
        campoMunicipio.value = dados.ibge;
        return;
    }

    selecao.addOption({ valor: dados.ibge, texto: dados.localidade });
    selecao.refreshOptions(false);
    selecao.setValue(dados.ibge, true);
}

/** So o que usamos do tom-select — evita depender do tipo completo aqui. */
interface TomSelectMinimo {
    addOption(dados: Record<string, unknown>): void;
    refreshOptions(abrir: boolean): void;
    setValue(valor: string, silencioso?: boolean): void;
}

async function buscar(campo: HTMLInputElement): Promise<void> {
    const cep = somenteDigitos(campo.value);

    // Menos de oito digitos e CEP pela metade, nao CEP errado: quem esta
    // digitando nao merece mensagem de erro a cada saida do campo.
    if (cep.length !== 8) {
        mensagem(campo, '');
        return;
    }

    // Guarda o ultimo CEP consultado no proprio campo: entrar e sair sem mudar
    // nada nao refaz a chamada, e nao sobrescreve o que a pessoa corrigiu a mao
    // depois do preenchimento automatico.
    if (campo.dataset.cepConsultado === cep) return;

    campo.dataset.cepConsultado = cep;
    campo.classList.add('js-carregando');

    try {
        const resposta = await fetch(`${VIACEP}/${cep}/json/`, { headers: { Accept: 'application/json' } });

        if (!resposta.ok) throw new Error(String(resposta.status));

        const dados = await resposta.json() as RespostaViaCep;

        // O ViaCEP devolve 200 com `{"erro": true}` para CEP inexistente — o
        // status HTTP sozinho nao serve de checagem.
        if (dados.erro) {
            mensagem(campo, 'CEP não encontrado.');
            return;
        }

        preencher(alvo(campo, 'cepLogradouro'), dados.logradouro ?? '');
        preencher(alvo(campo, 'cepBairro'), dados.bairro ?? '');
        preencherMunicipio(campo, dados);

        mensagem(campo, '');

        // O numero e a unica coisa que o CEP nunca traz. Levar o foco para la
        // continua o preenchimento de onde ele parou.
        alvo(campo, 'cepFoco')?.focus();

    } catch {
        // Rede fora, CORS, servico indisponivel: o campo nao trava e o resto do
        // endereco continua preenchivel a mao. Limpa a marca de consultado para
        // a proxima saida do campo tentar de novo.
        delete campo.dataset.cepConsultado;
        mensagem(campo, 'Não foi possível consultar o CEP agora. Preencha o endereço manualmente.');
    } finally {
        campo.classList.remove('js-carregando');
    }
}

export function iniciarBuscaDeCep(escopo: ParentNode = document): void {
    escopo.querySelectorAll<HTMLInputElement>('[data-cep]').forEach(campo => {
        campo.addEventListener('blur', () => { void buscar(campo); });
    });
}
